import { Button, Stack, useWindowProperties } from '@qred/components-library';
import React from 'react';
import { NestedObjects } from '~/declarations/nested-objects.d';
import useTranslate from '~/hooks/useTranslate';
import { TranslationsKeys } from '~/languages/translations.d';
import { PASSCODE_LENGTH } from './PasscodeInput';
import { usePasscodeFlow } from './usePasscodeFlow';

type TranslationKey = NestedObjects<TranslationsKeys>;

interface SuccessAction {
  label: TranslationKey;
  onClick: () => void;
}

interface PasscodeCardActionsProps {
  /** Whether the flow should default to the already-set view. */
  hasExistingPasscode: boolean;
  /** Omitted on the standalone page, which has no next action after success. */
  successAction?: SuccessAction;
  /** Present only where an existing passcode can be kept as-is, i.e. the offer step. */
  onKeepExisting?: () => void;
}

/**
 * The matching content renders separately; `usePasscodeFlow` keeps the two
 * in sync.
 */
export const PasscodeCardActions = ({
  hasExistingPasscode,
  successAction,
  onKeepExisting,
}: PasscodeCardActionsProps) => {
  const translate = useTranslate();
  const { isMobile } = useWindowProperties();
  const {
    view,
    isSubmitting,
    goToCreate,
    proceedToVerify,
    submitVerification,
  } = usePasscodeFlow(hasExistingPasscode);

  switch (view.kind) {
    case 'alreadySet':
      return (
        <Stack spacing="sm" align="center">
          <Button
            size="md"
            fullWidth={isMobile}
            dataCy="passcode_create_new_button"
            onClick={goToCreate}
          >
            {translate('CreatePasscode.CreateNewButton')}
          </Button>
          {onKeepExisting && (
            <Button
              size="md"
              variant="secondary"
              fullWidth={isMobile}
              dataCy="passcode_keep_existing_button"
              onClick={onKeepExisting}
            >
              {translate('CreatePasscode.KeepExistingButton')}
            </Button>
          )}
        </Stack>
      );

    case 'create':
      return (
        <Stack spacing="sm" align="center">
          <Button
            size="md"
            fullWidth={isMobile}
            dataCy="passcode_continue_button"
            disabled={view.passcode.length < PASSCODE_LENGTH}
            onClick={proceedToVerify}
          >
            {translate('CreatePasscode.ContinueButton')}
          </Button>
        </Stack>
      );

    case 'verify':
      return (
        <Stack spacing="sm" align="center">
          <Button
            size="md"
            fullWidth={isMobile}
            dataCy="passcode_verify_button"
            isLoading={isSubmitting}
            disabled={view.confirmation.length < PASSCODE_LENGTH}
            onClick={submitVerification}
          >
            {translate('CreatePasscode.VerifyButton')}
          </Button>
          <Button
            size="md"
            variant="secondary"
            fullWidth={isMobile}
            dataCy="passcode_back_button"
            disabled={isSubmitting}
            onClick={goToCreate}
          >
            {translate('CreatePasscode.BackButton')}
          </Button>
        </Stack>
      );

    case 'submitError':
      return null;

    case 'success':
      return successAction ? (
        <Stack spacing="sm" align="center">
          <Button
            size="md"
            fullWidth={isMobile}
            dataCy="passcode_success_button"
            onClick={successAction.onClick}
          >
            {translate(successAction.label)}
          </Button>
        </Stack>
      ) : null;

    default: {
      const exhaustiveCheck: never = view;
      return exhaustiveCheck;
    }
  }
};
